import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Row, Col, Input, Label, Collapse, Button } from "reactstrap";
import useCartStore from "../../stores/useCartStore";
import { dateAddYears } from "../../Utilities/dateUtils";
import {
  aplLicenseWillBeExtended,
  formatLicenseDate,
} from "../../Utilities/licenseHelper";
import { FormattedMessage, useIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LicenseTypes } from "../../constants/LicenseTypes";
import DataGrid from "../DataGrids/DataGrid";

export default function ShopStudentsSelect({
  studentsOnCurrentProgram,
  lastAddedProduct,
  addOrRemoveStudentFromCartItem,
  allStudentsWithLicenses,
  allStudents,
  preselectedStudents,
  removeManyFromCart,
  addManyToCart,
  allChecked,
  setAllChecked,
}) {
  const intl = useIntl();
  const [studentsForAdding, setStudentsForAdding] = useState([]);
  const [filteredStudentsForSelect, setFilteredStudentsForSelect] = useState(
    [],
  );
  const [filterStudentsBy, setFilteredStudentsBy] = useState("");
  const [studentsWithCurrentLicense, setStudentsWithCurrentLicense] = useState(
    [],
  );
  const [showStudentsWithLicense, setShowStudentsWithLicense] = useState(false);

  const columns = useMemo(
    () => [
      {
        id: "input",
        key: "input",
        header: intl.formatMessage({ id: "Choose" }),
        width: 100,
        sortable: false,
        cell: (props) => props.getValue(),
      },
      {
        id: "name",
        key: "name",
        header: intl.formatMessage({ id: "Name" }),
        width: 300,
      },
      {
        id: "class",
        key: "class",
        header: intl.formatMessage({ id: "Class" }),
        width: 300,
      },
      {
        id: "aplLicense",
        key: "aplLicense",
        header: intl.formatMessage({ id: "AplLicense" }),
        width: 400,
        sortable: false,
        cell: (props) => props.getValue(),
      },
    ],
    [intl],
  );

  const [rowsForSelect, setRowsForSelect] = useState([]);
  const [rowsForNotSelect, setRowsForNotSelect] = useState([]);
  const allStudentsList = useMemo(
    () =>
      Array.isArray(allStudents)
        ? allStudents
        : Array.isArray(allStudents?.result)
          ? allStudents.result
          : [],
    [allStudents],
  );
  const allStudentsWithLicensesList = useMemo(
    () => allStudentsWithLicenses?.data ?? [],
    [allStudentsWithLicenses?.data],
  );

  const cartResult = useCartStore((s) => s.result);
  const addManyStudentsToCartAndLastAdded = useCartStore(
    (s) => s.addManyStudentsToCartAndLastAdded,
  );

  const createLicensesFallback = (licenses) =>
    licenses ?? {
      programLicenseIds: [],
      aplLicenseValidFrom: null,
      aplLicenseValidTo: null,
      hasValidAplLicense: false,
    };

  const studentsOnCurrentProgramList = useMemo(
    () =>
      studentsOnCurrentProgram.map((stud) => ({
        ...stud,
        licenses: createLicensesFallback(stud.licenses),
      })),
    [studentsOnCurrentProgram],
  );

  useEffect(() => {
    //If generic apl license selected show all students
    if (lastAddedProduct.type === LicenseTypes.APLOnly) {
      const tempAllStudents = [];
      allStudentsList.forEach((stud) => {
        const licenses = allStudentsWithLicensesList.find(
          (studWL) => studWL.studentId === stud.id,
        );
        let studentToAdd = {
          name: stud.name,
          id: stud.id,
          class: stud.class,
          licenses: createLicensesFallback(licenses),
        };
        tempAllStudents.push(studentToAdd);
      });
      setStudentsForAdding(tempAllStudents);
      setFilteredStudentsForSelect(tempAllStudents);
      setStudentsWithCurrentLicense([]);
      //If pluslicense Show only students on current program
    } else if (
      lastAddedProduct.type === LicenseTypes.PlusLicense ||
      lastAddedProduct.type === LicenseTypes.StudentLicencePlus
    ) {
      setStudentsForAdding([...studentsOnCurrentProgramList]);
      setFilteredStudentsForSelect([...studentsOnCurrentProgramList]);
      setStudentsWithCurrentLicense([]);
      //StudentLicens, visa alla elever som inte har elevlicens
    } else if (lastAddedProduct.type === LicenseTypes.StudentLicense) {
      let tempStudentsForChoosing = studentsOnCurrentProgramList.filter(
        (stud) =>
          !stud.licenses.programLicenseIds.includes(
            lastAddedProduct.programLicenseId,
          ),
      );
      let tempStudentsForNotChoosing = studentsOnCurrentProgramList.filter(
        (stud) =>
          stud.licenses.programLicenseIds.includes(
            lastAddedProduct.programLicenseId,
          ),
      );
      setStudentsWithCurrentLicense(tempStudentsForNotChoosing);
      setStudentsForAdding(tempStudentsForChoosing);
      setFilteredStudentsForSelect(tempStudentsForChoosing);
    } else if (lastAddedProduct.isPlusPackage) {
      //Visa alla elever som inte har licens.
      const tempStudentsForChoosing = studentsOnCurrentProgramList.filter(
        (stud) =>
          !stud.licenses.programLicenseIds.includes(
            lastAddedProduct.studentLicense.programLicenseId,
          ),
      );
      const tempStudentsForNotChoosing = studentsOnCurrentProgramList.filter(
        (stud) =>
          stud.licenses.programLicenseIds.includes(
            lastAddedProduct.studentLicense.programLicenseId,
          ),
      );
      setStudentsWithCurrentLicense(tempStudentsForNotChoosing);
      setStudentsForAdding(tempStudentsForChoosing);
      setFilteredStudentsForSelect(tempStudentsForChoosing);
    }

    if (preselectedStudents && preselectedStudents.length > 0) {
      const preselectedIds = preselectedStudents.map((stud) => stud.id);
      const preselectedStudentsOnProgram = studentsOnCurrentProgramList.filter((stud) =>
        preselectedIds.includes(stud.id),
      );
      setStudentsForAdding(preselectedStudentsOnProgram);
      setFilteredStudentsForSelect(preselectedStudentsOnProgram);
    }
  }, [
    studentsOnCurrentProgram,
    lastAddedProduct,
    allStudents,
    allStudentsWithLicenses.data,
    allStudentsWithLicenses.isPending,
    preselectedStudents,
    allStudentsWithLicensesList,
    studentsOnCurrentProgramList,
    allStudentsList,
  ]);

  const autoCheckedLicenseKeyRef = useRef(null);

  useEffect(() => {
    if (!preselectedStudents || preselectedStudents.length === 0) {
      return;
    }
    if (!lastAddedProduct || cartResult.length === 0) {
      return;
    }

    const licenseKey = lastAddedProduct.isPlusPackage
      ? `${lastAddedProduct.studentLicense?.fortnoxNumber}-${lastAddedProduct.plusPackage?.fortnoxNumber}`
      : lastAddedProduct.fortnoxNumber;

    if (!licenseKey || autoCheckedLicenseKeyRef.current === licenseKey) {
      return;
    }

    if (studentsForAdding.length > 0) {
      if (lastAddedProduct.isPlusPackage) {
        const baseStudents = studentsForAdding.filter(
          (stud) =>
            !stud.licenses.programLicenseIds.includes(
              lastAddedProduct.studentLicense.programLicenseId,
            ),
        );
        addManyStudentsToCartAndLastAdded(
          baseStudents,
          lastAddedProduct.studentLicense.fortnoxNumber,
        );
        addManyStudentsToCartAndLastAdded(
          baseStudents,
          lastAddedProduct.plusPackage.fortnoxNumber,
        );
      } else {
        addManyToCart(studentsForAdding);
      }
      autoCheckedLicenseKeyRef.current = licenseKey;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentsForAdding]);

  useEffect(() => {
    if (filterStudentsBy.length > 0) {
      setFilteredStudentsForSelect(
        studentsForAdding.filter(
          (stud) =>
            stud.name.toLowerCase().includes(filterStudentsBy.toLowerCase()) ||
            stud.class.name
              .toLowerCase()
              .includes(filterStudentsBy.toLowerCase()),
        ),
      );
    } else {
      setFilteredStudentsForSelect(studentsForAdding);
    }
  }, [filterStudentsBy, studentsForAdding]);

  const handleToggleAllStudents = () => {
    if (allChecked) {
      removeManyFromCart(filteredStudentsForSelect);
      setAllChecked(false);
    } else {
      addManyToCart(filteredStudentsForSelect);
      setAllChecked(true);
    }
  };

  const handleGetCheckStatus = useCallback(
    (studentId) => {
      if (lastAddedProduct.isPlusPackage) {
        if (
          cartResult.some(
            (cartitem) =>
              cartitem.fortnoxNumber ===
                lastAddedProduct.studentLicense.fortnoxNumber &&
              cartitem.addedStudents.some(
                (addedstud) => addedstud.id === studentId,
              ),
          )
        ) {
          return true;
        } else {
          return false;
        }
      } else {
        if (
          cartResult.some(
            (cartitem) =>
              cartitem.fortnoxNumber === lastAddedProduct.fortnoxNumber &&
              cartitem.addedStudents.some(
                (addedstud) => addedstud.id === studentId,
              ),
          )
        ) {
          return true;
        } else {
          return false;
        }
      }
    },
    [lastAddedProduct, cartResult],
  );

  const renderAplLicenseCell = useCallback(
    (stud) => {
      if (
        lastAddedProduct.type === LicenseTypes.StudentLicense &&
        !(preselectedStudents && preselectedStudents.length > 0)
      ) {
        return "";
      }
      if (stud.licenses.aplLicenseValidTo === null) {
        return null;
      }
      const aplYears = lastAddedProduct.isPlusPackage
        ? lastAddedProduct.plusPackage?.years
        : lastAddedProduct.years;
      const aplValidTo = stud.licenses.aplLicenseValidTo;
      if (aplYears && aplLicenseWillBeExtended(stud.licenses)) {
        return (
          <FormattedMessage
            id="StudentAplLicenseWillBeExtended"
            values={{
              dateFrom: formatLicenseDate(stud.licenses.aplLicenseValidFrom),
              dateUntil: formatLicenseDate(aplValidTo),
              newDateUntil: formatLicenseDate(
                dateAddYears(aplValidTo, aplYears),
              ),
            }}
          />
        );
      }
      if (stud.licenses.hasValidAplLicense) {
        return (
          <>
            <FormattedMessage id="StudentHasAValidAplLicenseUntil" />{" "}
            {formatLicenseDate(stud.licenses.aplLicenseValidTo)}
          </>
        );
      }
      return (
        <FormattedMessage
          values={{
            dateFrom: formatLicenseDate(stud.licenses.aplLicenseValidFrom),
            dateUntil: formatLicenseDate(stud.licenses.aplLicenseValidTo),
          }}
          id="StudentHasAValidAplLicenseFromUntil"
        />
      );
    },
    [lastAddedProduct, preselectedStudents],
  );

  useEffect(() => {
    if (lastAddedProduct) {
      let tempStudentsForSelect = [];
      filteredStudentsForSelect.forEach((stud, i) => {
        let tempStudentToAdd = {
          input: null,
          name: null,
          class: null,
          aplLicense: null,
        };
        tempStudentToAdd.name = stud.name;
        tempStudentToAdd.input = (
          <Input
            style={{
              marginLeft: "1em",
              marginRight: "1em",
              marginTop: "0",
              position: "relative",
            }}
            type="checkbox"
            onChange={() => addOrRemoveStudentFromCartItem({ ...stud })}
            checked={handleGetCheckStatus(stud.id)}
          />
        );
        tempStudentToAdd.class = stud.class.name;
        tempStudentToAdd.aplLicense = renderAplLicenseCell(stud);
        tempStudentsForSelect.push(tempStudentToAdd);
      });
      setRowsForSelect(tempStudentsForSelect);
    }
  }, [
    filteredStudentsForSelect,
    addOrRemoveStudentFromCartItem,
    lastAddedProduct,
    cartResult,
    handleGetCheckStatus,
    renderAplLicenseCell,
    preselectedStudents,
  ]);

  useEffect(() => {
    let tempStudentsForNotSelect = [];
    studentsWithCurrentLicense.forEach((stud, i) => {
      let tempStudentToAdd = {
        input: null,
        name: null,
        class: null,
        aplLicense: null,
      };
      tempStudentToAdd.name = stud.name;
      tempStudentToAdd.input = (
        <Input
          style={{
            marginLeft: "1em",
            marginRight: "1em",
            marginTop: "0",
            position: "relative",
            cursor: "not-allowed",
          }}
          type="checkbox"
          checked={true}
          disabled={true}
        />
      );
      tempStudentToAdd.class = stud.class.name;
      tempStudentToAdd.aplLicense = renderAplLicenseCell(stud);
      tempStudentsForNotSelect.push(tempStudentToAdd);
    });
    setRowsForNotSelect(tempStudentsForNotSelect);
  }, [studentsWithCurrentLicense, renderAplLicenseCell]);

  return (
    <>
      <Row className="mb-2">
        <Col lg="4" md="4" sm="6" xs="12">
          <Input
            type="text"
            placeholder={intl.formatMessage({
              id: "FilterStudentsByNameOrClass",
            })}
            onChange={(data) => setFilteredStudentsBy(data.target.value)}
          ></Input>
        </Col>
      </Row>
      {!(preselectedStudents && preselectedStudents.length > 0) && (
        <Row>
          <Col>
            <Label for="checkAll">
              <FormattedMessage id="SelectSlashUnselectAll" />
            </Label>
            <Input
              style={{ marginLeft: ".5em" }}
              name="checkAll"
              type="checkbox"
              onChange={() => handleToggleAllStudents()}
              checked={allChecked}
            />
          </Col>
        </Row>
      )}

      <DataGrid
        rows={rowsForSelect}
        columns={columns}
        initialSorting={[{ id: "name", desc: false }]}
        noDataMessage={<FormattedMessage id="NoStudentsToChoose" />}
      />

      <>
        <Row className="mt-4">
          <Col>
            <Button
              color="primary"
              outline
              onClick={() =>
                setShowStudentsWithLicense(!showStudentsWithLicense)
              }
              className="mb-3"
            >
              {showStudentsWithLicense ? (
                <FontAwesomeIcon className="me-2" icon="chevron-down" />
              ) : (
                <FontAwesomeIcon className="me-2" icon="chevron-right" />
              )}
              <FormattedMessage id="ShowStudentsWithLicense" />
            </Button>
          </Col>
        </Row>
        <Collapse isOpen={showStudentsWithLicense}>
          <DataGrid
            rows={rowsForNotSelect}
            columns={columns}
            initialSorting={[{ id: "name", desc: false }]}
            noDataMessage={<FormattedMessage id="NoStudents" />}
          />
        </Collapse>
      </>
    </>
  );
}
